import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';

import { finalize, Subject, takeUntil } from 'rxjs';
import { LoadingController } from 'app/shared/helpers/loading.controller';
import { ValueItem } from 'app/shared/interfaces/value-item.interface';
import { MainMapService } from 'app/shared/components/main-map/main-map.service';
import {
  AttributesFormPrint,
  LayoutOrientationType,
  PaperSizeDimensionType,
  PrintMapService,
} from 'app/shared/services/print-map.service';
import { ToastService } from 'app/shared/services/toast.service';
import { AppConfigService } from 'app/shared/services/app-config.service';
import { TranslationService } from '../../../../services/translation.service';
import { CodedValueItem } from 'app/shared/interfaces/coded-value-item.interface';
import { ScreenService } from 'app/shared/services/screen.service';

interface AttributesFormLike {
  titleMap?: FormControl<string>;
  author?: FormControl<string>;
  typeFile: FormControl<AttributesFormPrint['typeFile']>;
  paperSizeDimension: FormControl<AttributesFormPrint['paperSizeDimension']>;
  layoutOrientation: FormControl<AttributesFormPrint['layoutOrientation']>;
  includesCaption: FormControl<boolean>;
}

const defaultAttributesForm: Partial<AttributesFormPrint> = {
  paperSizeDimension: PaperSizeDimensionType.A4,
  layoutOrientation: LayoutOrientationType.Portrait,
  includesCaption: true,
};

@Component({
  selector: 'app-print-map-form',
  templateUrl: 'print-map-form.component.html',
  styleUrls: ['print-map-form.component.scss'],
})
export class PrintMapFormComponent implements OnInit, OnDestroy {
  @Input()
  mapView: __esri.MapView;

  formGroup: FormGroup<AttributesFormLike>;

  typeFileList: { value: string; label?: string }[];

  paperSizeDimensionList: ValueItem<
    AttributesFormPrint['paperSizeDimension']
  >[];

  layoutOrientationList: CodedValueItem<
    AttributesFormPrint['layoutOrientation']
  >[];

  get isAuthorVisible() {
    return !(this.appConfig.siiaMode || this.appConfig.externalMode);
  }

  get isTitleVisible() {
    return !this.appConfig.siiaMode;
  }

  get isLegendVisible() {
    return !this.appConfig.siiaMode;
  }

  textMaxLengthDefault = 100;

  get isBtnSubmitDisabled() {
    return this.formGroup.invalid;
  }

  typeFileSelectDisplayExpr = (item) => {
    if (!item) {
      return item;
    }

    return item.label ?? item.value;
  };

  isMobileModeObservable = this.screenService.observeIsMobile();

  private lifecycle = new Subject<void>();

  constructor(
    private formBuilder: FormBuilder,
    private mainMapService: MainMapService,
    private printMapService: PrintMapService,
    private appConfig: AppConfigService,
    private toastService: ToastService,
    private loadingController: LoadingController,
    private screenService: ScreenService,
    public translationService: TranslationService
  ) {}

  ngOnInit(): void {
    this.formGroup = this.formBuilder.group<AttributesFormLike>({
      titleMap: this.formBuilder.control<string>(null, [
        Validators.maxLength(this.textMaxLengthDefault),
      ]),
      author: this.formBuilder.control<string>(null, [
        Validators.maxLength(this.textMaxLengthDefault),
      ]),
      typeFile: this.formBuilder.control<AttributesFormPrint['typeFile']>(
        null,
        Validators.required
      ),
      paperSizeDimension: this.formBuilder.control<
        AttributesFormPrint['paperSizeDimension']
      >(null, Validators.required),
      layoutOrientation: this.formBuilder.control<
        AttributesFormPrint['layoutOrientation']
      >(null, Validators.required),
      includesCaption: this.formBuilder.control<boolean>(null),
    });

    this.formGroup.reset(defaultAttributesForm);

    this.typeFileList = [
      { value: 'PDF' },
      { value: 'PNG8', label: 'PNG' },
      { value: 'JPG' },
    ];

    this.paperSizeDimensionList = Object.values(PaperSizeDimensionType).map(
      (value) => {
        return { value };
      }
    );

    this.layoutOrientationList = [
      {
        label: 'Portrait',
        value: LayoutOrientationType.Portrait,
      },
      {
        label: 'Landscape',
        value: LayoutOrientationType.Landscape,
      },
    ];

    this.mainMapService.onRequestClear
      .pipe(takeUntil(this.lifecycle))
      .subscribe(() => {
        this.formGroup.reset(defaultAttributesForm);
      });
  }

  ngOnDestroy(): void {
    this.lifecycle.next();
    this.lifecycle.complete();
  }

  onSubmit(e) {
    e.preventDefault();

    if (this.loadingController.isShown() || this.isBtnSubmitDisabled) {
      return;
    }

    const {
      titleMap,
      author,
      typeFile,
      paperSizeDimension,
      layoutOrientation,
      includesCaption,
    } = this.formGroup.getRawValue();

    this.loadingController.show();
    this.printMapService
      .printMap(this.mapView, {
        titleMap,
        author,
        typeFile,
        includesCaption,
        mode: this.appConfig.siiaMode
          ? 'Siia'
          : this.appConfig.externalMode
          ? 'External'
          : 'Internal',
        paperSizeDimension,
        layoutOrientation,
      })
      .pipe(
        finalize(() => {
          this.loadingController.hide();
        }),
        takeUntil(this.lifecycle)
      )
      .subscribe({
        next: ({ url }) => {
          this.printMapService.openPrintWindow(url);
        },
        error: (response) => {
          if (!response) {
            return;
          }

          this.toastService.error(
            this.translationService.getCurrentLanguage().warning,
            this.translationService.getCurrentLanguage()['printing-failed']
          );
        },
      });
  }
}
